import './utils/resizeObserverErrorGuard';
import './config/themePaletteInit';
import './config/datePickerLocale';
import 'react-datepicker/dist/react-datepicker.css';
import './utils/chartjsSetup';
import { applyBrandingToDocument } from './config/branding';
import React, { Suspense } from 'react';
import { lazyWithRetry as lazy } from './utils/lazyWithRetry';
import { stripReleaseCacheBustParam } from './utils/appReleaseNavigate';
import { clearStaleChunkReloadFlag, isChunkLoadError, reloadOnceForStaleChunk } from './utils/chunkLoadError';
import RouteErrorBoundary from './components/common/RouteErrorBoundary';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouterProvider } from 'react-router-dom';
import App from './App';
import './assets/styles/index.css';

import { PermissionRoute, PrivateRoute } from './components/validroute/AuthRoute';
import { PERM } from './utils/accessPermissions';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { zhCN as muiDatePickerZhCN } from '@mui/x-date-pickers/locales';

import store from './store';
import AuthCardLayout from './layouts/AuthCardLayout';
import { RouteFallback } from './components/Loader';
import AppProvider from './providers/AppProvider';
import OrderPerformanceFeaturesProvider from './providers/OrderPerformanceFeaturesProvider';
import OrderPerformanceFeatureRoute from './components/validroute/OrderPerformanceFeatureRoute';

// 首屏同步：登录与登录后首页
import LoginoutScreen from './screens/LoginoutScreen';
import LoginScreen from './screens/LoginScreen';
import RegisterScreen from './screens/RegisterScreen';
import DashboardScreen from './screens/dashboard/DashboardScreen';

// 其余路由懒加载，按进入页面再拉对应 chunk
const MarketingDashboardDemoScreen = lazy(() => import('./screens/demo/MarketingDashboardDemoScreen'));
const LoginDemoScreen = lazy(() => import('./screens/demo/LoginDemoScreen'));
const LoginDemo2Screen = lazy(() => import('./screens/demo/LoginDemo2Screen'));
const LoginDemo3Screen = lazy(() => import('./screens/demo/LoginDemo3Screen'));
const LoginDemo4Screen = lazy(() => import('./screens/demo/LoginDemo4Screen'));
const ChartWorkbenchDemoScreen = lazy(() => import('./screens/demo/chart-workbench/ChartWorkbenchDemoScreen'));
const TotalDebtDetailScreen = lazy(() => import('./screens/dashboard/TotalDebtDetailScreen'));
const CreateOrderScreen = lazy(() => import('./screens/order/create-an-order/CreateOrderScreen'));
const OrderCreateSuccessScreen = lazy(() => import('./screens/order/create-an-order/OrderCreateSuccessScreen'));
const QuotationScreen = lazy(() => import('./screens/order/quotation/QuotationScreen'));
const CustomerListScreen = lazy(() => import('./screens/customer/CustomerListScreen'));
const OrderListScreen = lazy(() => import('./screens/order/OrderListScreen'));
const ProfileScreen = lazy(() => import('./screens/ProfileScreen'));
const BrandListAdminScreen = lazy(() => import('./screens/admin/BrandListAdminScreen'));
const UserListAdminScreen = lazy(() => import('./screens/admin/UserListAdminScreen'));
const UserCreateAdminScreen = lazy(() => import('./screens/admin/UserCreateAdminScreen'));
const UserEditAdminScreen = lazy(() => import('./screens/admin/UserEditAdminScreen'));
const LoginLogScreen = lazy(() => import('./screens/admin/LoginLogScreen'));
const AccessDeniedScreen = lazy(() => import('./screens/AccessDeniedScreen'));
const RolePermissionAdminScreen = lazy(() => import('./screens/admin/RolePermissionAdminScreen'));
const SupplierListAdminScreen = lazy(() => import('./screens/admin/SupplierListAdminScreen'));
const BulkRematchPerformanceRulesAdminScreen = lazy(() => import('./screens/admin/BulkRematchPerformanceRulesAdminScreen'));
const ApprovalListScreen = lazy(() => import('./screens/approval/ApprovalListScreen'));
const CustomerApprovalDetailScreen = lazy(() => import('./screens/approval/CustomerApprovalDetailScreen'));
const OrderApprovalDetailScreen = lazy(() => import('./screens/approval/OrderApprovalDetailScreen'));
const QuotaApprovalDetailScreen = lazy(() => import('./screens/approval/QuotaApprovalDetailScreen'));
const FinalCostAdjustmentApprovalDetailScreen = lazy(() => import('./screens/approval/FinalCostAdjustmentApprovalDetailScreen.jsx'));
const NotificationListScreen = lazy(() => import('./screens/notification/NotificationListScreen'));
const GenechampUserListScreen = lazy(() => import('./screens/order/manager/GenechampUserListScreen'));
const OrderListManagerScreen = lazy(() => import('./screens/order/manager/OrderListManagerScreen'));
const PerformanceRuleListManagerScreen = lazy(() => import('./screens/performance-rule/PerformanceRuleListManagerScreen'));
const OrderDetailScreen = lazy(() => import('./screens/order/OrderDetailScreen'));
const OrderDraftListScreen = lazy(() => import('./screens/order/OrderDraftListScreen'));
const QuotationListScreen = lazy(() => import('./screens/order/QuotationListScreen'));
const ProductListScreen = lazy(() => import('./screens/product/ProductListScreen'));
const ProductDetailScreen = lazy(() => import('./screens/product/ProductDetailScreen'));
const StatisticsScreen = lazy(() => import('./screens/statistics/StatisticsScreen'));

window.addEventListener('unhandledrejection', (event) => {
	if (isChunkLoadError(event.reason)) {
		event.preventDefault();
		reloadOnceForStaleChunk();
	}
});

const router = createBrowserRouter(
	createRoutesFromElements(
		<Route errorElement={<RouteErrorBoundary />}>
			{/*- ------------- Authentication ---------------------------  */}
			{/*- ------------- Card ---------------------------  */}
			{/* 正式登录：自管海滩背景与无框壳，不走 AuthCardLayout */}
			<Route index={true} path='/' element={<LoginScreen />} />
			<Route element={<AuthCardLayout />}>
				<Route path='/register' element={<RegisterScreen />} />
				<Route path='/loginout' element={<LoginoutScreen />} />
			</Route>

			{/* Figma Make 登录视觉 Demo（独立全屏，不走 AuthCardLayout / 正式鉴权） */}
			<Route path='/demo/login' element={<LoginDemoScreen />} />
			<Route path='/demo/login2' element={<LoginDemo2Screen />} />
			<Route path='/demo/login3' element={<LoginDemo3Screen />} />
			<Route path='/demo/login4' element={<LoginDemo4Screen />} />
			{/* Figma Make 统一图表工作台 Demo（TS，假数据，独立全屏） */}
			<Route path='/demo/chart-workbench' element={<ChartWorkbenchDemoScreen />} />

			<Route element={<App />}>
				<Route path='/demo/dashboard' element={<MarketingDashboardDemoScreen />} />
				<Route path='' element={<PrivateRoute />}>
					{/* ------------ DashboardScreen ----------- */}
					<Route path='/access-denied' element={<AccessDeniedScreen />} />
					<Route path='/dashboard' element={<DashboardScreen />} />
					<Route path='' element={<PermissionRoute permission={PERM.MENU_DASHBOARD_STAT_CARDS} />}>
						<Route path='/dashboard/total-debt-detail' element={<TotalDebtDetailScreen />} />
					</Route>

					<Route path='' element={<PermissionRoute permission={PERM.MENU_PROFILE} />}>
						<Route path='/profile' element={<ProfileScreen />} />
					</Route>
					<Route path='' element={<PermissionRoute permission={[PERM.MENU_APPROVAL_APPLICATIONS, PERM.MENU_APPROVAL_RECORDS]} />}>
						<Route path='/approval/list' element={<ApprovalListScreen />} />
						<Route path='/approval/customer/detail/:id' element={<CustomerApprovalDetailScreen />} />
						<Route path='/approval/order/detail/:approvalId' element={<OrderApprovalDetailScreen />} />
						<Route path='/approval/order/:orderId/detail/:approvalId' element={<OrderApprovalDetailScreen />} />
						<Route path='/approval/final-cost/:orderId/detail/:approvalId' element={<FinalCostAdjustmentApprovalDetailScreen />} />
						<Route path='/approval/quota/detail/:id' element={<QuotaApprovalDetailScreen />} />
					</Route>
					<Route path='' element={<PermissionRoute permission={PERM.MENU_NOTIFICATION_LIST} />}>
						<Route path='/notificationlist' element={<NotificationListScreen />} />
					</Route>
					<Route path='' element={<PermissionRoute permission={PERM.MENU_PRODUCT_LIST} />}>
						<Route path='/productlist' element={<ProductListScreen />} />
						<Route path='/product/:productId' element={<ProductDetailScreen />} />
					</Route>
					<Route path='' element={<PermissionRoute permission={PERM.MENU_CUSTOMER_LIST} />}>
						<Route path='/customerlist' element={<CustomerListScreen />} />
					</Route>
					<Route path='' element={<PermissionRoute permission={PERM.MENU_ORDER_DRAFT} />}>
						<Route path='/editlist' element={<OrderDraftListScreen />} />
					</Route>
					<Route path='' element={<PermissionRoute permission={PERM.MENU_ORDER_QUOTATION} />}>
						<Route path='/quotationlist' element={<QuotationListScreen />} />
						<Route path='/order/quotation/:id' element={<QuotationScreen />} />
					</Route>
					<Route path='' element={<PermissionRoute permission={PERM.MENU_ORDER_CREATE} />}>
						<Route path='/order/create/new' element={<CreateOrderScreen />} />
						<Route path='/order/create/:rejectedOrderId/rejected' element={<CreateOrderScreen />} />
						<Route path='' element={<PermissionRoute permission={PERM.MENU_ORDER_QUOTATION} />}>
							<Route path='/order/create/new/quotation' element={<CreateOrderScreen />} />
						</Route>
						<Route path='' element={<PermissionRoute permission={PERM.MENU_ORDER_DRAFT} />}>
							<Route path='/order/create/:draftId/draft' element={<CreateOrderScreen />} />
						</Route>
						<Route path='' element={<PermissionRoute permission={PERM.MENU_ORDER_QUOTATION} />}>
							<Route path='/order/create/:quotationDocId/quotation' element={<CreateOrderScreen />} />
						</Route>
					</Route>
					<Route path='' element={<PermissionRoute permission={PERM.ORDER_QUERY} />}>
						<Route path='/order/detail' element={<OrderDetailScreen />} />
						<Route path='/order/detail/:pageIndex' element={<OrderDetailScreen />} />
						<Route path='/order/:id/success' element={<OrderCreateSuccessScreen />} />
					</Route>
					<Route path='/manager/editlist' element={<Navigate to='/editlist' replace />} />
					<Route path='/finance/editlist' element={<Navigate to='/editlist' replace />} />
					<Route path='/buyer/editlist' element={<Navigate to='/editlist' replace />} />
					<Route path='/user/editlist' element={<Navigate to='/editlist' replace />} />
					<Route path='/assistant/editlist' element={<Navigate to='/editlist' replace />} />
					<Route path='/sale/editlist' element={<Navigate to='/editlist' replace />} />
					<Route path='/admin/editlist' element={<Navigate to='/editlist' replace />} />
					<Route path='/order/quotation' element={<Navigate to='/quotationlist' replace />} />
					<Route path='' element={<PermissionRoute permission={PERM.MENU_STATISTICS_VIEW} />}>
						<Route path='/statistics' element={<StatisticsScreen />} />
					</Route>

					<Route path='' element={<PermissionRoute permission={PERM.ORDER_QUERY} />}>
						<Route path='/orderlist' element={<OrderListManagerScreen />} />
					</Route>
					<Route path='' element={<PermissionRoute permission={[PERM.PERFORMANCE_QUERY, PERM.PERFORMANCE_UPDATE]} requireAll />}>
						<Route element={<OrderPerformanceFeatureRoute feature='rulesManagement' />}>
							<Route path='/manager/performancerules' element={<PerformanceRuleListManagerScreen />} />
						</Route>
					</Route>
					<Route path='' element={<PermissionRoute permission={PERM.PERFORMANCE_BULK_REMATCH} />}>
						<Route element={<OrderPerformanceFeatureRoute feature='bulkRematch' />}>
							<Route path='/manager/bulk-rematch-performance-rules' element={<BulkRematchPerformanceRulesAdminScreen />} />
						</Route>
					</Route>
					<Route path='' element={<PermissionRoute permission={PERM.USER_QUERY} />}>
						<Route path='/manager/userlist' element={<GenechampUserListScreen />} />
					</Route>
					<Route path='' element={<PermissionRoute permission={PERM.ORDER_QUERY} />}>
						<Route path='/user/orderlist' element={<OrderListScreen />} />
					</Route>
					<Route path='/admin/customerlist' element={<Navigate to='/customerlist' replace />} />
					<Route path='/admin/productlist' element={<Navigate to='/productlist' replace />} />
					<Route path='/admin/userlist' element={<Navigate to='/userlist' replace />} />
					<Route path='' element={<PermissionRoute permission={PERM.MENU_USER_LIST} />}>
						<Route path='/userlist' element={<UserListAdminScreen />} />
					</Route>
					<Route path='' element={<PermissionRoute permission={PERM.USER_UPDATE} />}>
						<Route path='/admin/user/create' element={<UserCreateAdminScreen />} />
						<Route path='/admin/user/:id/edit' element={<UserEditAdminScreen />} />
						<Route path='/admin/role-permissions' element={<RolePermissionAdminScreen />} />
					</Route>
					<Route path='' element={<PermissionRoute permission={PERM.MENU_BRAND_LIST} />}>
						<Route path='/admin/brandlist' element={<BrandListAdminScreen />} />
					</Route>
					<Route path='' element={<PermissionRoute permission={PERM.MENU_LOGINLOG_LIST} />}>
						<Route path='/admin/loginlogs' element={<LoginLogScreen />} />
					</Route>
					<Route path='' element={<PermissionRoute permission={PERM.MENU_SUPPLIER_LIST} />}>
						<Route path='/admin/supplierlist' element={<SupplierListAdminScreen />} />
					</Route>
					<Route path='' element={<PermissionRoute permission={[PERM.PERFORMANCE_QUERY, PERM.PERFORMANCE_UPDATE]} requireAll />}>
						<Route element={<OrderPerformanceFeatureRoute feature='rulesManagement' />}>
							<Route path='/admin/performancerules' element={<Navigate to='/manager/performancerules' replace />} />
						</Route>
					</Route>
				</Route>
			</Route>
		</Route>,
	),
);

applyBrandingToDocument();
stripReleaseCacheBustParam();

window.setTimeout(clearStaleChunkReloadFlag, 15000);

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
	<React.StrictMode>
		<HelmetProvider>
			<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='zh-cn' localeText={muiDatePickerZhCN.components.MuiLocalizationProvider.defaultProps.localeText}>
				<Provider store={store}>
					<OrderPerformanceFeaturesProvider>
						<AppProvider>
							<Suspense fallback={<RouteFallback />}>
								<RouterProvider router={router} />
							</Suspense>
						</AppProvider>
					</OrderPerformanceFeaturesProvider>
				</Provider>
			</LocalizationProvider>
		</HelmetProvider>
	</React.StrictMode>,
);
